import { useState, useEffect } from 'react';
import { Key, Shield, Eye, EyeOff, Save, Trash2, CheckCircle, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import AdvertisingSetupGuides from './CredentialSetupGuides';

interface Credential {
  key: string;
  label: string;
  category: string;
  source: 'override' | 'platform' | 'none';
  masked_value: string | null;
  is_set: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  advertising: 'Advertising Platforms',
  payments: 'Payments',
  email: 'Email',
  search: 'Search',
  ai: 'AI & Suppliers',
  auth: 'Authentication',
  storage: 'Storage',
};

const CATEGORY_ORDER = ['advertising', 'payments', 'ai', 'email', 'search', 'auth', 'storage'];

function SourceBadge({ source }: { source: string }) {
  if (source === 'override') {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Site Override</span>;
  }
  if (source === 'platform') {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Platform</span>;
  }
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-500">Not Set</span>;
}

export default function PlatformCredentials() {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successKey, setSuccessKey] = useState<string | null>(null);

  async function loadCredentials() {
    try {
      const res = await api.get('/admin/credentials') as any;
      setCredentials(res.credentials || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadCredentials(); }, []);

  async function handleSave(key: string) {
    if (!editValue.trim()) return;
    setSaving(true);
    try {
      await api.put(`/admin/credentials/${key}`, { value: editValue.trim() });
      setEditingKey(null);
      setEditValue('');
      setShowValue(false);
      setSuccessKey(key);
      setTimeout(() => setSuccessKey(null), 2000);
      await loadCredentials();
    } catch (err: any) {
      setError(err.message || 'Failed to save credential');
    } finally {
      setSaving(false);
    }
  }

  async function handleClear(key: string) {
    setSaving(true);
    try {
      await api.delete(`/admin/credentials/${key}`);
      setSuccessKey(key);
      setTimeout(() => setSuccessKey(null), 2000);
      await loadCredentials();
    } catch (err: any) {
      setError(err.message || 'Failed to clear credential');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  const grouped = CATEGORY_ORDER
    .map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat] || cat,
      items: credentials.filter((c) => c.category === cat),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Key className="w-6 h-6" />
            Platform Credentials
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            API keys inherited from the platform. Child sites can override with their own keys.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <Shield className="w-4 h-4" />
          Values are never shown in full
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {grouped.map((group) => (
        <Card key={group.category}>
          <h2 className="text-lg font-semibold text-text-primary mb-4">{group.label}</h2>
          {group.category === 'advertising' && <AdvertisingSetupGuides />}
          <div className="divide-y divide-stone-100">
            {group.items.map((cred) => (
              <div key={cred.key} className="py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text-primary">{cred.label}</span>
                    <SourceBadge source={cred.source} />
                    {successKey === cred.key && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                  <div className="text-xs text-text-muted font-mono mt-0.5">
                    {cred.masked_value || 'Not configured'}
                  </div>
                </div>

                {editingKey === cred.key ? (
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-none">
                      <Input
                        type={showValue ? 'text' : 'password'}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="Enter new value"
                        className="text-sm w-full sm:w-64 pr-8"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowValue(!showValue)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                      >
                        {showValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <Button
                      size="sm"
                      variant="boutique"
                      onClick={() => handleSave(cred.key)}
                      disabled={saving || !editValue.trim()}
                      isLoading={saving}
                    >
                      <Save className="w-3.5 h-3.5" />
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setEditingKey(null); setEditValue(''); setShowValue(false); }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setEditingKey(cred.key); setEditValue(''); }}
                    >
                      {cred.source === 'override' ? 'Update' : 'Override'}
                    </Button>
                    {cred.source === 'override' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleClear(cred.key)}
                        disabled={saving}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
